import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch, getApiHost } from '../utils/api';

interface Props {
  open: boolean;
  onClose: () => void;
  triggerRef: React.RefObject<HTMLElement>;
}

const formatDnetBalance = (balance: number): string => {
  const fmt = (n: number): string => {
    if (n >= 100) return Math.round(n).toString();
    if (n >= 10) return n.toFixed(1).replace(/\.0$/, '');
    return n.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
  };
  if (balance >= 1_000_000_000) return `${fmt(balance / 1_000_000_000)}B`;
  if (balance >= 1_000_000) return `${fmt(balance / 1_000_000)}M`;
  if (balance >= 1_000) return `${fmt(balance / 1_000)}K`;
  return Math.round(balance).toString();
};

const truncate = (s: string) => `${s.slice(0, 4)}…${s.slice(-4)}`;

const AccountDropdown: React.FC<Props> = ({ open, onClose, triggerRef }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);
  const [dnetBalance, setDnetBalance] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);

  // Click-outside + Esc
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, triggerRef]);

  // Fetch fresh DNET balance when opened (header also caches it, but keep
  // dropdown self-sufficient so the number is always current)
  useEffect(() => {
    if (!open) return;
    if (!user?.walletAddress) {
      setDnetBalance(0);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`${getApiHost()}/api/burn/dnet-balance/${user.walletAddress}`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setDnetBalance(data.balance);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [open, user?.walletAddress]);

  if (!open || !user) return null;

  const copyAddress = () => {
    if (!user.walletAddress) return;
    navigator.clipboard.writeText(user.walletAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const go = (path: string) => {
    onClose();
    navigate(path);
  };

  const handleSignOut = () => {
    onClose();
    logout();
  };


  return (
    <div
      ref={panelRef}
      role="menu"
      className="absolute right-0 top-full mt-2 w-72 bg-crypto-gray border border-gray-700 rounded-xl shadow-2xl overflow-hidden z-50 animate-fadeIn"
    >
      {/* Wallet address — only thing the button doesn't already show */}
      {user.walletAddress && (
        <div className="p-4 border-b border-gray-800">
          <button
            onClick={copyAddress}
            className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-crypto-accent transition-colors font-mono group"
            title="Copy address"
          >
            <span>{truncate(user.walletAddress)}</span>
            <svg className="w-3 h-3 opacity-60 group-hover:opacity-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            {copied && <span className="text-crypto-accent">copied</span>}
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="px-4 py-3 border-b border-gray-800">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-400">$DNET balance</span>
          <span className="text-white font-medium">
            {dnetBalance !== null ? formatDnetBalance(dnetBalance) : (user.dnetBalance !== undefined ? formatDnetBalance(user.dnetBalance) : '…')}
          </span>
        </div>
      </div>

      {/* Nav */}
      <nav className="py-1">
        <MenuItem icon="fa-user" label="Account" onClick={() => go('/profile')} />
        <MenuItem icon="fa-coins" label="Credits" onClick={() => go('/profile/credits')} value={user.credits.toLocaleString()} />
        <MenuItem icon="fa-key" label="API Keys" onClick={() => go('/api-keys')} />
        <MenuItem icon="fa-shield-halved" label="Security" onClick={() => go('/profile/security')} />
      </nav>

      {/* Sign out */}
      <div className="border-t border-gray-800 py-1">
        <button
          onClick={handleSignOut}
          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
          role="menuitem"
        >
          <i className="fas fa-right-from-bracket w-4 text-center"></i>
          Sign Out
        </button>
      </div>
    </div>
  );
};

const MenuItem: React.FC<{ icon: string; label: string; onClick: () => void; value?: string }> = ({ icon, label, onClick, value }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-800/60 hover:text-white transition-colors"
    role="menuitem"
  >
    <i className={`fas ${icon} w-4 text-center text-gray-500`}></i>
    <span className="flex-1 text-left">{label}</span>
    {value && <span className="text-white font-medium">{value}</span>}
  </button>
);

export default AccountDropdown;
